import { Fragment, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
    {
        title: "Personal",
        fields: [
            ["first_name", "First name"],
            ["last_name", "Last name"],
            ["email", "Email"],
            ["phone", "Phone"],
            ["age", "Age"],
            ["pronouns", "Pronouns"],
            ["identity_note", "Identity note"],
            ["city", "City"],
            ["state", "State"],
            ["occupation", "Occupation"],
        ],
    },
    {
        title: "Relationship",
        fields: [
            ["relationship_status", "Status"],
            ["partner_applying", "Partner applying?"],
            ["partner_included", "Partner included"],
            ["partner_name", "Partner name"],
            ["years_together", "Years together"],
            ["dating_dynamic", "Dating dynamic"],
            ["single_intent", "Single intent"],
        ],
    },
    {
        title: "Story",
        fields: [
            ["describe_yourself", "Describe yourself"],
            ["community_meaning", "Community means…"],
            ["misunderstood_by_others", "Most misunderstood"],
            ["conflict_style", "Conflict style"],
            ["cast_impression", "Cast impression"],
        ],
    },
    {
        title: "Availability",
        fields: [
            ["availability", "General availability"],
            ["available_sep_25_27", "Sep 25–27 available"],
            ["willing_to_drive_2_3_hours", "Willing to drive 2–3 hrs"],
            ["on_camera_comfort", "On-camera comfort"],
        ],
    },
    {
        title: "Media",
        fields: [
            ["photo_keys", "Photos"],
            ["video_key", "Video key"],
            ["video_url", "Video URL"],
            ["instagram", "Instagram"],
            ["facebook", "Facebook"],
            ["tiktok", "TikTok"],
            ["other_socials", "Other socials"],
            ["referral", "How they heard"],
        ],
    },
    {
        title: "Consents",
        fields: [
            ["consent_filming", "Filming"],
            ["consent_share", "Share"],
            ["consent_solo_submission", "Solo submission"],
            ["consent_truth", "Truth"],
        ],
    },
];

function formatValue(field, value) {
    if (value === null || value === undefined || value === "") {
        return <em className="text-[color:var(--cream-muted)]">—</em>;
    }
    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }
    if (Array.isArray(value)) {
        if (value.length === 0) return <em className="text-[color:var(--cream-muted)]">—</em>;
        if (field === "photo_keys") {
            return (
                <div className="flex flex-wrap gap-2">
                    {value.map((k) => (
                        <a
                            key={k}
                            href={`${BACKEND_URL}/api/uploads/${k}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                            data-testid={`draft-photo-${k}`}
                        >
                            <img
                                src={`${BACKEND_URL}/api/uploads/${k}`}
                                alt="photo"
                                className="w-20 h-20 object-cover border border-[color:var(--espresso)] hover:opacity-80 transition"
                            />
                        </a>
                    ))}
                </div>
            );
        }
        return value.join(", ");
    }
    if (field === "video_key" && typeof value === "string") {
        return (
            <a href={`${BACKEND_URL}/api/uploads/${value}`} target="_blank" rel="noopener noreferrer" className="link-underline text-[color:var(--gold)] break-all">
                {value} ↗
            </a>
        );
    }
    if (field === "video_url" && typeof value === "string" && value.startsWith("http")) {
        return <a href={value} target="_blank" rel="noopener noreferrer" className="link-underline text-[color:var(--gold)] break-all">{value} ↗</a>;
    }
    if (field === "instagram" && typeof value === "string" && value.trim()) {
        const h = value.replace(/^@/, "").trim();
        return <a href={`https://instagram.com/${h}`} target="_blank" rel="noopener noreferrer" className="link-underline text-[color:var(--gold)]">@{h}</a>;
    }
    return <span className="whitespace-pre-wrap">{String(value)}</span>;
}

function DraftDetail({ form }) {
    if (!form || typeof form !== "object") {
        return <p className="text-sm text-[color:var(--cream-muted)] italic">No form data captured yet.</p>;
    }
    const knownKeys = new Set(SECTIONS.flatMap((s) => s.fields.map(([k]) => k)));
    const extraKeys = Object.keys(form).filter((k) => !knownKeys.has(k) && k !== "email");
    return (
        <div className="space-y-6">
            {SECTIONS.map((section) => {
                const rows = section.fields.filter(([k]) => k in form && form[k] !== undefined && form[k] !== null && form[k] !== "");
                if (rows.length === 0) return null;
                return (
                    <div key={section.title}>
                        <p className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--gold)] mb-2">{section.title}</p>
                        <dl className="grid md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                            {rows.map(([k, label]) => (
                                <div key={k} className="flex flex-col border-b border-[color:var(--espresso)]/40 py-2">
                                    <dt className="text-[0.6rem] uppercase tracking-[0.22em] text-[color:var(--cream-muted)] mb-1">{label}</dt>
                                    <dd className="text-[color:var(--soft-cream)] leading-relaxed" data-testid={`draft-field-${k}`}>{formatValue(k, form[k])}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                );
            })}
            {extraKeys.length > 0 && (
                <div>
                    <p className="text-[0.62rem] uppercase tracking-[0.24em] text-[color:var(--gold)] mb-2">Additional</p>
                    <dl className="grid md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                        {extraKeys.map((k) => (
                            <div key={k} className="flex flex-col border-b border-[color:var(--espresso)]/40 py-2">
                                <dt className="text-[0.6rem] uppercase tracking-[0.22em] text-[color:var(--cream-muted)] mb-1">{k.replace(/_/g, " ")}</dt>
                                <dd className="text-[color:var(--soft-cream)] leading-relaxed">{formatValue(k, form[k])}</dd>
                            </div>
                        ))}
                    </dl>
                </div>
            )}
        </div>
    );
}

export default function DraftsPanel({ drafts, onChanged }) {
    const [query, setQuery] = useState("");
    const [busy, setBusy] = useState(false);
    const [expanded, setExpanded] = useState(null);
    const filtered = drafts.filter((d) => {
        if (!query) return true;
        const q = query.toLowerCase();
        return `${d.first_name} ${d.last_name} ${d.email} ${d.city} ${d.occupation}`.toLowerCase().includes(q);
    });

    const notifyFix = async () => {
        const total = drafts.length;
        if (total === 0) { toast("No unfinished drafts to notify."); return; }
        const force = window.confirm(
            `Notify all ${total} draft applicant${total === 1 ? "" : "s"} that the video upload bug is fixed?\n\n` +
            `• Email + SMS (if phone on file) will be sent\n` +
            `• Already-notified applicants are skipped\n\n` +
            `Click Cancel to skip. Click OK to send.\n\n` +
            `Force-resend to previously-notified applicants too? (You'll be asked in a moment.)`
        );
        if (!force) return;
        const alsoResend = window.confirm(
            "Also RESEND to draft applicants who were already notified about the fix?\n\n" +
            "• OK = re-send to everyone (including already-notified)\n" +
            "• Cancel = only notify people who haven't been contacted yet"
        );
        setBusy(true);
        try {
            const r = await api.post(`/admin/casting/drafts/notify-fix${alsoResend ? "?force=true" : ""}`);
            const s = r.data || {};
            toast.success(
                `Emails sent: ${s.email_sent || 0} · SMS sent: ${s.sms_sent || 0} · Skipped: ${(s.skipped_submitted || 0) + (s.skipped_already_notified || 0)}`,
                { duration: 10000 }
            );
        } catch {
            toast.error("Couldn't run the broadcast — check backend logs.");
        } finally {
            setBusy(false);
        }
    };

    const notifySendLater = async () => {
        const total = drafts.length;
        if (total === 0) { toast("No unfinished drafts to notify."); return; }
        const confirmed = window.confirm(
            `Email all ${total} draft applicant${total === 1 ? "" : "s"} about the text/email video option?\n\n` +
            `• Sends an outreach email with 380-200-0084 and casting@mrsblackinlove.org\n` +
            `• Already-notified applicants are skipped by default\n\n` +
            `Click OK to send.`
        );
        if (!confirmed) return;
        const alsoResend = window.confirm(
            "Also RESEND to applicants already notified about the send-later option?\n\n" +
            "• OK = resend to everyone\n" +
            "• Cancel = only notify people who haven't been contacted yet"
        );
        setBusy(true);
        try {
            const r = await api.post(`/admin/casting/drafts/notify-send-later${alsoResend ? "?force=true" : ""}`);
            const s = r.data || {};
            toast.success(
                `Send-later emails sent: ${s.email_sent || 0} · Skipped: ${(s.skipped_submitted || 0) + (s.skipped_already_notified || 0)} · Failed: ${s.email_failed || 0}`,
                { duration: 10000 }
            );
        } catch {
            toast.error("Couldn't run the broadcast — check backend logs.");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (token) => {
        if (!window.confirm("Delete this draft? The applicant's resume link will stop working.")) return;
        try {
            await api.delete(`/admin/casting/drafts/${token}`);
            toast.success("Draft removed.");
            onChanged?.();
        } catch { toast.error("Delete failed."); }
    };

    const nudge = async (token, email, tone = "nudge") => {
        try {
            await api.post(`/admin/casting/drafts/${token}/nudge?tone=${tone}`);
            const label = { warm: "warm", urgent: "urgent", deadline: "deadline" }[tone] || "";
            toast.success(`${label ? label.charAt(0).toUpperCase() + label.slice(1) + " reminder" : "Reminder"} sent to ${email}.`);
        } catch { toast.error("Couldn't send the reminder right now."); }
    };

    const copyResumeLink = async (token) => {
        const url = `${window.location.origin}/apply?resume=${token}`;
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Resume link copied. Send it to give them a nudge.");
        } catch { toast.error("Couldn't copy — long press to copy manually."); }
    };

    return (
        <div data-testid="drafts-panel">
            <div className="flex flex-col md:flex-row md:items-baseline justify-between gap-4 mb-6">
                <div>
                    <p className="eyebrow">Started but not submitted</p>
                    <p className="text-sm text-[color:var(--cream-muted)] mt-2 max-w-2xl">
                        Applicants who saved progress via "Save & finish later" (or hit Continue after entering their email) but haven't submitted. Click the arrow on any row to see everything they've filled in so far. Copy their private resume link to send a gentle nudge — the link is theirs alone and stays valid for 30 days.
                    </p>
                </div>
                <span className="text-[0.65rem] uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">{filtered.length} of {drafts.length}</span>
            </div>
            <div className="mb-6 flex flex-wrap items-center gap-3">
                <button
                    type="button"
                    onClick={notifyFix}
                    disabled={busy || drafts.length === 0}
                    className="btn-gold btn-gold-solid text-[0.68rem] disabled:opacity-40"
                    data-testid="drafts-notify-fix"
                    title="Send a one-off email + SMS to every draft applicant telling them the video upload bug is fixed"
                >{busy ? "Sending…" : `Notify all — video upload fix (${drafts.length})`}</button>
                <button
                    type="button"
                    onClick={notifySendLater}
                    disabled={busy || drafts.length === 0}
                    className="btn-gold text-[0.68rem] disabled:opacity-40"
                    data-testid="drafts-notify-send-later"
                    title="Email every draft applicant about the text-it / email-it video option (380-200-0084 · casting@mrsblackinlove.org)"
                >{busy ? "Sending…" : `Notify all — send-later option (${drafts.length})`}</button>
                <p className="text-[0.65rem] uppercase tracking-[0.18em] text-[color:var(--cream-muted)]">One-off broadcast · already-notified skipped by default</p>
            </div>
            <input
                className="input-line w-full mb-6"
                placeholder="Search email, name, city, occupation…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="drafts-search"
            />
            {filtered.length === 0 ? (
                <div className="border border-dashed border-[color:var(--espresso)] p-10 text-center" data-testid="drafts-empty">
                    <p className="text-sm text-[color:var(--cream-muted)]">
                        {drafts.length === 0 ? "No unfinished applications right now. When someone starts and pauses, they'll appear here." : "No drafts match that search."}
                    </p>
                </div>
            ) : (
                <table className="w-full text-sm">
                    <thead className="text-[color:var(--gold)] text-left">
                        <tr className="border-b border-[color:var(--espresso)]">
                            <th className="py-3 w-8"></th>
                            <th>Applicant</th>
                            <th>Location</th>
                            <th>Occupation</th>
                            <th>Started</th>
                            <th>Expires</th>
                            <th className="text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((d) => {
                            const name = `${d.first_name} ${d.last_name}`.trim() || "—";
                            const location = [d.city, d.state].filter(Boolean).join(", ") || "—";
                            const started = d.created_at ? new Date(d.created_at).toLocaleDateString() : "—";
                            const expires = d.expires_at ? new Date(d.expires_at).toLocaleDateString() : "—";
                            const open = expanded === d.token;
                            return (
                                <Fragment key={d.token}>
                                    <tr className="border-b border-[color:var(--espresso)]/50" data-testid={`draft-row-${d.token}`}>
                                        <td className="py-3">
                                            <button
                                                onClick={() => setExpanded(open ? null : d.token)}
                                                className="text-[color:var(--gold)] hover:text-[color:var(--soft-cream)] p-1"
                                                aria-label={open ? "Collapse" : "Expand"}
                                                data-testid={`draft-expand-${d.token}`}
                                            >{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</button>
                                        </td>
                                        <td className="py-3">
                                            <div className="font-serif text-lg text-[color:var(--soft-cream)]">{name}</div>
                                            <div className="text-xs text-[color:var(--cream-muted)]">{d.email}</div>
                                        </td>
                                        <td>{location}</td>
                                        <td>{d.occupation || "—"}</td>
                                        <td>{started}</td>
                                        <td className="text-[color:var(--cream-muted)]">{expires}</td>
                                        <td className="text-right">
                                            <div className="flex flex-wrap justify-end gap-2 items-center">
                                                <div className="inline-flex border border-[color:var(--espresso)] divide-x divide-[color:var(--espresso)]" data-testid={`draft-nudge-group-${d.token}`}>
                                                    <button onClick={() => nudge(d.token, d.email, "warm")} className="px-2 py-1 text-[0.6rem] uppercase tracking-[0.16em] hover:bg-[color:var(--gold)] hover:text-[color:var(--warm-black)]" title="Warm, no pressure" data-testid={`draft-nudge-warm-${d.token}`}>Warm</button>
                                                    <button onClick={() => nudge(d.token, d.email, "urgent")} className="px-2 py-1 text-[0.6rem] uppercase tracking-[0.16em] hover:bg-[color:var(--gold)] hover:text-[color:var(--warm-black)]" title="Time-sensitive nudge" data-testid={`draft-nudge-urgent-${d.token}`}>Urgent</button>
                                                    <button onClick={() => nudge(d.token, d.email, "deadline")} className="px-2 py-1 text-[0.6rem] uppercase tracking-[0.16em] hover:bg-[color:var(--gold)] hover:text-[color:var(--warm-black)]" title="Casting closes soon" data-testid={`draft-nudge-deadline-${d.token}`}>Deadline</button>
                                                </div>
                                                <button onClick={() => copyResumeLink(d.token)} className="link-underline text-xs" data-testid={`draft-copy-${d.token}`}>Copy link</button>
                                                <button onClick={() => remove(d.token)} className="link-underline text-xs text-[color:var(--wine)]" data-testid={`draft-delete-${d.token}`}>Delete</button>
                                            </div>
                                        </td>
                                    </tr>
                                    {open && (
                                        <tr className="bg-[color:var(--espresso)]/10 border-b border-[color:var(--espresso)]/50" data-testid={`draft-detail-${d.token}`}>
                                            <td></td>
                                            <td colSpan={6} className="py-6 pr-6">
                                                <DraftDetail form={d.form} />
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}
